import React, { useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
const AnalyticsPage: React.FC = () => {
  const {
    monthlyAverages,
    readings
  } = useData();
  // Get data for time of day analysis (by hour)
  const hourlyData = useMemo(() => {
    const hourCounts: Record<number, {
      count: number;
      total: number;
    }> = {};
    // Initialize hours
    for (let i = 0; i < 24; i++) {
      hourCounts[i] = {
        count: 0,
        total: 0
      };
    }
    // Aggregate readings by hour
    readings.forEach(reading => {
      const hour = new Date(reading.timestamp).getHours();
      hourCounts[hour].count += 1;
      hourCounts[hour].total += reading.value;
    });
    // Calculate averages
    return Object.entries(hourCounts).map(([hour, data]) => ({
      hour: `${hour}:00`,
      average: data.count > 0 ? data.total / data.count : 0
    }));
  }, [readings]);
  return <div className="space-y-6" data-id="element-17">
      <h2 className="text-2xl font-bold" data-id="element-18">Analytics</h2>
      {/* Monthly Averages */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-19">
        <h3 className="text-lg font-semibold mb-4" data-id="element-20">
          Monthly Average Consumption (Last 12 Months)
        </h3>
        <div className="h-80" data-id="element-21">
          <ResponsiveContainer width="100%" height="100%" data-id="element-22">
            <BarChart data={monthlyAverages} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 30
          }} data-id="element-23">
              <CartesianGrid strokeDasharray="3 3" data-id="element-24" />
              <XAxis dataKey="month" angle={-45} textAnchor="end" height={70} tick={{
              fontSize: 12
            }} data-id="element-25" />
              <YAxis data-id="element-26" />
              <Tooltip formatter={value => [`${value.toFixed(2)} kWh`, 'Average']} data-id="element-27" />
              <Legend data-id="element-28" />
              <Bar dataKey="average" name="Average Consumption (kWh)" fill="#3b82f6" data-id="element-29" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Time of Day Analysis */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-30">
        <h3 className="text-lg font-semibold mb-4" data-id="element-31">
          Consumption by Time of Day
        </h3>
        <div className="h-80" data-id="element-32">
          <ResponsiveContainer width="100%" height="100%" data-id="element-33">
            <LineChart data={hourlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-34">
              <CartesianGrid strokeDasharray="3 3" data-id="element-35" />
              <XAxis dataKey="hour" data-id="element-36" />
              <YAxis data-id="element-37" />
              <Tooltip formatter={value => [`${value.toFixed(2)} kWh`, 'Average']} data-id="element-38" />
              <Legend data-id="element-39" />
              <Line type="monotone" dataKey="average" name="Average Consumption (kWh)" stroke="#8884d8" strokeWidth={2} dot={{
              r: 3
            }} data-id="element-40" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-sm text-gray-500 mt-2" data-id="element-41">
          This chart shows the average energy consumption by hour of day,
          helping identify peak usage times.
        </p>
      </div>
      <div className="bg-white rounded-lg shadow p-4" data-id="element-42">
        <h3 className="text-lg font-semibold mb-4" data-id="element-43">Analytics Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-44">
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-45">
            <h4 className="font-semibold text-blue-800" data-id="element-46">Peak Usage Times</h4>
            <p className="text-sm text-gray-700 mt-2" data-id="element-47">
              Energy consumption typically peaks between 6 PM and 9 PM when most
              household appliances are active. Consider shifting some
              energy-intensive activities to off-peak hours to reduce costs.
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg" data-id="element-48">
            <h4 className="font-semibold text-green-800" data-id="element-49">Seasonal Patterns</h4>
            <p className="text-sm text-gray-700 mt-2" data-id="element-50">
              Your data shows higher consumption during winter and summer
              months, likely due to heating and cooling needs. Improving
              insulation could help reduce these seasonal peaks.
            </p>
          </div>
        </div>
      </div>
    </div>;
};
export default AnalyticsPage;